// src/App.jsx
import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import DashboardPage from "./pages/DashboardPage";
import ShopPerformancePage from "./pages/ShopPerfomancePage";
import CreatorPerformancePage from "./pages/CreatorPerfomancePage";
import Admin from "./pages/Admin";
import "./App.css";
import TikTokLogin from "./components/TikTokLogin";
import AdminShopDetailPage from "./pages/AdminShopDetailPage";
import BackendMetrices from "./pages/BackendMetrices";
import InventoryTrackerPage from "./pages/InventoryTrackerPage";
import TopSellingSkuPage from "./pages/TopSellingSkuPage";
import { FormActionProvider } from "./context/FormActionContext";
import { ThemeProvider } from "./context/ThemeContext";
import "./styles/light-theme.css";
import SampleDistributionLogPage from "./pages/SampleDistributionLogPage";
import AlertsPage from "./pages/AlertsPage";
import Login from "./components/Login";
import Signup from "./components/Signup";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import PromotionStatusPage from "./pages/PromotionStatusPage";
import PromotionDetailPage from "./pages/PromotionDetailPage";
import AffiliateCommunicationPage from "./pages/AffiliateCommunicationPage";
import RefundOrdersPage from "./pages/RefundOrdersPage";
import PackageShipmentPage from "./pages/PackageShipmentPage";
import PublicDashboardPage from "./pages/PublicDashboardPage";
import MyShopsLayoutPage from "./pages/MyShopsPage";
import AffiliatesPage from "./pages/AffiliatesPage";
import OrganicPage from "./pages/OrganicPage";
import PaidAdsPage from "./pages/PaidAdsPage";
import ShopHealthPage from "./pages/ShopHealthPage";
import PnLPage from "./pages/PnLPage";
import CommandCentrePage from "./pages/CommandCentrePage";
import Loader from "./components/Loader";
import SubscriptionPage from "./pages/SubscriptionPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import TikTokCallbackPage from "./pages/TikTokCallbackPage";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <Loader fullPage text="OS." bottomSpinner={true} />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const location = useLocation();

  React.useEffect(() => {
    const path = location.pathname;
    let title = "Shop OS";

    const titleMap = {
      "/": "Login",
      "/dashboard": "Dashboard",
      "/affiliates": "Affiliates",
      "/organic": "Organic",
      "/paid-ads": "Paid Ads",
      "/shop-health": "Shop Health",
      "/pnl": "P&L",
      "/products": "Products",
      "/creator-performance": "Creator Performance",
      "/alerts": "Alerts",
      "/backend-metrics": "Backend Metrics",
      "/myshops": "My Shops",
      "/admin": "Admin",
      "/settings": "Settings",
      "/signup": "Data Entry",
      "/tiktoklogin": "Connect Shop",
      "/privacy-policy": "Privacy Policy",
      "/terms-of-service": "Terms of Service",
      "/subscription": "Subscription",
      "/payment-success": "Payment Success",
    };

    // Check for exact match
    if (titleMap[path]) {
      title += ` | ${titleMap[path]}`;
    } else if (path.startsWith("/admin/shop/")) {
      title += " | Shop Detail";
    } else if (path.startsWith("/backend-metrics/")) {
      // specific backend metrics sub-pages
      if (path.includes("inventory")) title += " | Inventory Tracker";
      else if (path.includes("top-skus")) title += " | Top Selling SKUs";
      else if (path.includes("samples")) title += " | Sample Logs";
      else if (path.includes("communication"))
        title += " | Affiliate Communication";
      else if (path.includes("promotions")) title += " | Promotions";
      else if (path.includes("refunds")) title += " | Refunds";
      else if (path.includes("shipment")) title += " | Shipment";
      else title += " | Backend Metrics";
    }

    document.title = title;
  }, [location]);

  return (
    <div className="app-container">
      <Routes>
        {/* Public Routes */}
        <Route path="/shared/:token" element={<DashboardPage isPublic={true} />} />

        {/* Auth Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/tiktoklogin" element={<TikTokLogin />} />
        <Route path="/tiktok/callback" element={<TikTokCallbackPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliates"
          element={
            <ProtectedRoute>
              <AffiliatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organic"
          element={
            <ProtectedRoute>
              <OrganicPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/paid-ads"
          element={
            <ProtectedRoute>
              <PaidAdsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/command-centre"
          element={
            <ProtectedRoute>
              <CommandCentrePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop-health"
          element={
            <ProtectedRoute>
              <ShopHealthPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pnl"
          element={
            <ProtectedRoute>
              <PnLPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ShopPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/creator-performance"
          element={
            <ProtectedRoute>
              <CreatorPerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute>
              <AlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics"
          element={
            <ProtectedRoute>
              <BackendMetrices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/inventory"
          element={
            <ProtectedRoute>
              <InventoryTrackerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/top-skus"
          element={
            <ProtectedRoute>
              <TopSellingSkuPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/samples"
          element={
            <ProtectedRoute>
              <SampleDistributionLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/communication"
          element={
            <ProtectedRoute>
              <AffiliateCommunicationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/promotions"
          element={
            <ProtectedRoute>
              <PromotionStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/promotions/:activityId"
          element={
            <ProtectedRoute>
              <PromotionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/refunds"
          element={
            <ProtectedRoute>
              <RefundOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-metrics/shipment"
          element={
            <ProtectedRoute>
              <PackageShipmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/myshops"
          element={
            <ProtectedRoute>
              <MyShopsLayoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/shop/:brandId"
          element={
            <ProtectedRoute>
              <AdminShopDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              {React.createElement(
                require("./pages/GeneralSettingsPage").default
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FormActionProvider>
          <Router>
            <AppRoutes />
          </Router>
        </FormActionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
